import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Building, Loader2, MapPin, PlayCircle, Square, Star, ExternalLink,
  Phone, Mail, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useIsPrivileged } from '@/hooks/useAuth';
import { useCancelJob } from '@/modules/sales-corp/api';
import { useKioskSites, useRunKioskSearch, useSetKioskStatus, useDraftKioskEmail } from './api';
import type { KioskSite, KioskStatus } from './types';

const CITIES = ['Mumbai', 'Bengaluru'] as const;
type City = (typeof CITIES)[number];

const KIND_LABEL: Record<string, string> = {
  business_park: 'Business park',
  it_park: 'IT / tech park',
  office_complex: 'Office complex',
  commercial_tower: 'Commercial tower',
  corporate_campus: 'Corporate campus',
  coworking: 'Coworking',
  other: 'Other',
};

const STATUSES: KioskStatus[] = ['prospect', 'shortlisted', 'contacted', 'negotiating', 'installed', 'rejected'];
const STATUS_LABEL: Record<KioskStatus, string> = {
  prospect: 'Prospect', shortlisted: 'Shortlisted', contacted: 'Contacted',
  negotiating: 'Negotiating', installed: 'Installed', rejected: 'Rejected',
};
const STATUS_TONE: Record<KioskStatus, string> = {
  prospect: 'text-muted-foreground',
  shortlisted: 'text-[#51BFAA]',
  contacted: 'text-[#4A9FE0]',
  negotiating: 'text-[#D9943A]',
  installed: 'text-[#3FA66A]',
  rejected: 'text-[#B15599]',
};

function priorityBadge(p: number) {
  if (p >= 3) return { label: 'High', cls: 'bg-[#3FA66A] text-white' };
  if (p === 2) return { label: 'Med', cls: 'bg-[#D9943A] text-white' };
  return { label: 'Low', cls: 'bg-muted text-muted-foreground' };
}

/** Kiosk site prospecting — corporate parks / office hubs where Flax could install a kiosk. */
export function KioskTab() {
  const qc = useQueryClient();
  const privileged = useIsPrivileged();
  const [city, setCity] = useState<City>('Mumbai');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [openId, setOpenId] = useState<string | null>(null);

  const sitesQ = useKioskSites(city);
  const runSearch = useRunKioskSearch();
  const cancelJob = useCancelJob();
  const isRunning = runSearch.isPending;

  const sites = useMemo(() => {
    let r = sitesQ.data ?? [];
    if (statusFilter) r = r.filter((s) => s.status === statusFilter);
    return r;
  }, [sitesQ.data, statusFilter]);

  const summary = useMemo(() => {
    const all = sitesQ.data ?? [];
    return {
      total: all.length,
      high: all.filter((s) => s.priority >= 3).length,
      withContact: all.filter((s) => s.contact_email || s.contact_phone).length,
      installed: all.filter((s) => s.status === 'installed').length,
    };
  }, [sitesQ.data]);

  function run() {
    // Refetch on settle (not just success): the search can run long enough that
    // the browser request times out (524) while the edge function keeps writing —
    // refetching anyway surfaces whatever it saved.
    runSearch.mutate({ city }, {
      onSettled: () => qc.invalidateQueries({ queryKey: ['sales', 'kiosk-sites'] }),
    });
  }

  return (
    <div className="space-y-4">
      {/* controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-border/60 p-0.5">
          {CITIES.map((c) => (
            <button
              key={c}
              onClick={() => { setCity(c); setOpenId(null); }}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                city === c ? 'bg-[#51BFAA] text-white' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {privileged ? (
            <>
              {isRunning && (
                <Button size="sm" variant="destructive" onClick={() => cancelJob.mutate('kiosk')} disabled={cancelJob.isPending}>
                  <Square className="h-3.5 w-3.5" /> Stop
                </Button>
              )}
              <Button size="sm" onClick={run} disabled={isRunning}>
                {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                Find kiosk sites · {city}
              </Button>
            </>
          ) : (
            <span className="text-2xs text-muted-foreground">Kiosk search is run by Gurmeet.</span>
          )}
        </div>
      </div>

      {/* run status */}
      {(isRunning || runSearch.data || runSearch.error) && (
        <div className="rounded-md border border-border/60 bg-card/40 px-3 py-2 text-xs">
          {isRunning && <span className="text-muted-foreground">Scanning {city}'s business hubs via Google Places… this can take a minute or two. Hit <strong>Stop</strong> to cancel.</span>}
          {!isRunning && runSearch.data && (
            <span className="text-foreground">
              {runSearch.data.cancelled ? 'Stopped — ' : runSearch.data.deadline_hit ? 'Partial (hit time limit) — ' : 'Done — '}
              {runSearch.data.sites_found} sites ({runSearch.data.sites_new} new) across {runSearch.data.localities} localities ·
              {' '}{runSearch.data.api_calls} API calls · ~${runSearch.data.cost_estimate_usd}
              {runSearch.data.deadline_hit ? ' · run again to continue' : ''}
            </span>
          )}
          {runSearch.error && (
            <span className="text-[#B15599]">
              {String(runSearch.error)} — the search may still be saving in the background; the list below refreshed with whatever completed.
            </span>
          )}
        </div>
      )}

      {/* filters + summary */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-border/60 bg-transparent px-2 text-sm outline-none focus:border-[#51BFAA]"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <span>{summary.total} sites</span>·<span>{summary.high} high-footfall</span>·
          <span>{summary.withContact} with contact</span>·<span>{summary.installed} installed</span>
        </div>
      </div>

      {/* table */}
      <Card>
        <CardContent className="p-0">
          {sitesQ.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : sites.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-muted-foreground">
              <Building className="h-6 w-6" />
              No kiosk sites yet for {city}. Hit <strong>Find kiosk sites</strong> to scan its corporate hubs.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr className="border-b border-border/60">
                    <th className="w-6 px-2 py-2"></th>
                    <th className="px-3 py-2 font-medium">Site</th>
                    <th className="px-3 py-2 font-medium">Locality</th>
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 font-medium">Footfall</th>
                    <th className="px-3 py-2 font-medium">Priority</th>
                    <th className="px-3 py-2 font-medium">Contact</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sites.map((s) => {
                    const pb = priorityBadge(s.priority);
                    const open = openId === s.id;
                    const hasContact = !!(s.contact_email || s.contact_phone || s.phone);
                    return (
                      <KioskRow
                        key={s.id}
                        site={s}
                        open={open}
                        onToggle={() => setOpenId(open ? null : s.id)}
                        priorityBadge={pb}
                        hasContact={hasContact}
                        privileged={privileged}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KioskRow({
  site, open, onToggle, priorityBadge: pb, hasContact, privileged,
}: {
  site: KioskSite;
  open: boolean;
  onToggle: () => void;
  priorityBadge: { label: string; cls: string };
  hasContact: boolean;
  privileged: boolean;
}) {
  const setStatus = useSetKioskStatus();
  return (
    <>
      <tr className="border-b border-border/40 align-top hover:bg-accent/40">
        <td className="px-2 py-2">
          <button onClick={onToggle} className="text-muted-foreground hover:text-foreground">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="cursor-pointer px-3 py-2" onClick={onToggle}>
          <div className="font-medium">{site.name}</div>
          <div className="flex items-center gap-2 text-2xs text-muted-foreground">
            {site.rating != null && (
              <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 fill-[#D9943A] text-[#D9943A]" />{site.rating}</span>
            )}
            {site.website && (
              <a href={site.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 hover:text-[#51BFAA]"><ExternalLink className="h-3 w-3" /> site</a>
            )}
          </div>
        </td>
        <td className="px-3 py-2 text-muted-foreground">{site.locality ?? '—'}</td>
        <td className="px-3 py-2"><Badge variant="outline" className="font-normal">{KIND_LABEL[site.kind] ?? site.kind}</Badge></td>
        <td className="px-3 py-2 font-mono text-xs">{site.user_ratings_total?.toLocaleString() ?? '—'}</td>
        <td className="px-3 py-2"><span className={cn('rounded px-1.5 py-0.5 text-2xs font-semibold', pb.cls)}>{pb.label}</span></td>
        <td className="px-3 py-2">
          {hasContact
            ? <span className="inline-flex items-center gap-1 text-2xs text-[#51BFAA]"><Phone className="h-3 w-3" /> yes</span>
            : <span className="text-2xs text-muted-foreground">—</span>}
        </td>
        <td className="px-3 py-2">
          {privileged ? (
            <select
              value={site.status}
              onChange={(e) => setStatus.mutate({ id: site.id, status: e.target.value as KioskStatus })}
              className={cn('h-7 rounded-md border border-border/60 bg-transparent px-1.5 text-xs outline-none focus:border-[#51BFAA]', STATUS_TONE[site.status])}
            >
              {STATUSES.map((st) => <option key={st} value={st}>{STATUS_LABEL[st]}</option>)}
            </select>
          ) : (
            <span className={cn('text-xs font-medium', STATUS_TONE[site.status])}>{STATUS_LABEL[site.status]}</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-border/40 bg-muted/20">
          <td />
          <td colSpan={7} className="px-3 py-3">
            <KioskDetail site={site} privileged={privileged} />
          </td>
        </tr>
      )}
    </>
  );
}

/** Expanded panel: how to reach the venue + an editable leasing contact + a one-click Gmail draft. */
function KioskDetail({ site, privileged }: { site: KioskSite; privileged: boolean }) {
  const setStatus = useSetKioskStatus();
  const draft = useDraftKioskEmail();
  const [f, setF] = useState({
    contact_name: site.contact_name ?? '',
    contact_email: site.contact_email ?? '',
    contact_phone: site.contact_phone ?? '',
    notes: site.notes ?? '',
  });
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  const dirty =
    f.contact_name !== (site.contact_name ?? '') ||
    f.contact_email !== (site.contact_email ?? '') ||
    f.contact_phone !== (site.contact_phone ?? '') ||
    f.notes !== (site.notes ?? '');

  const maps = site.maps_uri
    || (site.lat != null && site.lng != null
      ? `https://www.google.com/maps/search/?api=1&query=${site.lat},${site.lng}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(site.name + ' ' + site.city)}`);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* how to reach the venue */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground">How to reach the venue</p>
        {site.address && <p className="text-xs text-muted-foreground">{site.address}</p>}
        <div className="flex flex-wrap gap-2">
          {site.phone && (
            <a href={`tel:${site.phone}`} className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2.5 py-1.5 text-xs hover:border-[#51BFAA]">
              <Phone className="h-3.5 w-3.5" /> {site.phone}
            </a>
          )}
          <a href={maps} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2.5 py-1.5 text-xs hover:border-[#51BFAA]">
            <MapPin className="h-3.5 w-3.5" /> Map
          </a>
          {site.website && (
            <a href={site.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2.5 py-1.5 text-xs hover:border-[#51BFAA]">
              <ExternalLink className="h-3.5 w-3.5" /> Website
            </a>
          )}
        </div>
        <p className="text-2xs text-muted-foreground">
          Corporate parks are run by a property / facilities team. Call the front desk above to get the leasing contact, then save it here.
        </p>
      </div>

      {/* leasing contact + outreach */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground">Leasing / facility contact</p>
        {privileged ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              <input value={f.contact_name} onChange={(e) => set('contact_name', e.target.value)} placeholder="Contact name"
                className="h-8 rounded-md border border-border/60 bg-transparent px-2 text-xs outline-none focus:border-[#51BFAA]" />
              <input value={f.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} placeholder="Phone"
                className="h-8 rounded-md border border-border/60 bg-transparent px-2 text-xs outline-none focus:border-[#51BFAA]" />
            </div>
            <input type="email" value={f.contact_email} onChange={(e) => set('contact_email', e.target.value)} placeholder="Email (for the intro draft)"
              className="h-8 w-full rounded-md border border-border/60 bg-transparent px-2 text-xs outline-none focus:border-[#51BFAA]" />
            <textarea value={f.notes} onChange={(e) => set('notes', e.target.value)} rows={2} placeholder="Notes — who you spoke to, space on offer, next step…"
              className="w-full rounded-md border border-border/60 bg-transparent px-2 py-1.5 text-xs outline-none focus:border-[#51BFAA]" />
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" disabled={!dirty || setStatus.isPending}
                onClick={() => setStatus.mutate({ id: site.id, contact_name: f.contact_name, contact_email: f.contact_email, contact_phone: f.contact_phone, notes: f.notes })}>
                {setStatus.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save contact
              </Button>
              <Button size="sm" disabled={!f.contact_email || draft.isPending}
                onClick={() => draft.mutate({ id: site.id, to_email: f.contact_email || undefined })}>
                {draft.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Draft intro email
              </Button>
              {site.last_drafted_at && (
                <span className="text-2xs text-muted-foreground">Drafted {new Date(site.last_drafted_at).toLocaleDateString()}</span>
              )}
            </div>
            {draft.data && (
              <p className={cn('text-2xs', draft.data.drafted ? 'text-[#3FA66A]' : 'text-muted-foreground')}>{draft.data.note || (draft.data.error ? String(draft.data.error) : '')}</p>
            )}
            {draft.error && <p className="text-2xs text-[#B15599]">{String(draft.error)}</p>}
          </>
        ) : (
          <div className="space-y-1 text-xs text-muted-foreground">
            <p>{site.contact_name || '—'}</p>
            {site.contact_email && <p>{site.contact_email}</p>}
            {site.contact_phone && <p>{site.contact_phone}</p>}
            {site.notes && <p className="italic">{site.notes}</p>}
            <p className="text-2xs">Outreach drafts are created by Gurmeet.</p>
          </div>
        )}
        <p className="text-2xs text-muted-foreground">The intro email is parked in Gmail Drafts — a human reviews and sends it. Nothing auto-sends.</p>
      </div>
    </div>
  );
}
